import _ from 'lodash';
import type { NextPage } from 'next';
import { useCallback } from 'react';
import { getPageLanguage } from '@app/utils';
import {
  Filters,
  HadronConfig,
  IParams,
  isPartnerProfileSlugError,
  Language,
  PartnerProfileEnriched,
  ProfilePageType,
  SEOProps,
  SortingMethod,
  UserDetails,
  WixNextApiRequest,
  WixNextApiResponse,
} from '@common/types';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { ProfileContextProvider } from '@components/Contexts/ProfileContext';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import NotFound from '@components/Profile/NotFound';
import NotValidEuEmptyState from '@components/Profile/NotValidEuEmptyState';
import Profile from '@components/Profile';
import ProfileHeader from '@components/ProfileHeader';
import createAccountCertificationsAPI from '@services/AccountCertificationsAPI';
import createCurrencyAPI from '@services/CurrencyAPI';
import createLanguagesAPI from '@services/LanguagesAPI';
import createPartnerTraceableTraderAPI from '@services/PartnerTraceableTraderAPI';
import createSeoAPI from '@services/ProfileSeoAPI';
import createProvidersAPI from '@services/ProvidersAPI';
import createServicesAPI from '@services/ServicesAPI';
import createTemplateListingAPI from '@services/TemplateListingAPI';
import createUserAPI from '@services/UserAPI';
import createInitialStateService from '@services/InitialStateService';
import { PROFILE_NOT_EU_VALID, PROFILE_NOT_FOUND } from '@utils/constants';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { WixDesignSystemProvider } from '@wix/design-system';
import { GeoData, web } from '@wix/wix-common-aspects';
import log from '@wix/wix-log';
import he from 'he';
import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import { calculateTraceabilityFlags } from '@utils/traceabilityUtils';

const WixPartner: NextPage<{
  partnerProfile: PartnerProfileEnriched;
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  staticLocaleUrl: string;
  geo: GeoData;
  language: string;
  languages: Array<Language>;
  origin: string;
  entry: any;
  isMyProfile: boolean;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  error: string;
  host: string;
  seo: SEOProps;
  userCurrency: string;
  currencyConversionRate: number;
  shouldDisplayTraceabilityData: boolean;
  taxRate: number;
  numberOfTemplates: number;
  templatesNumberOfPages: number;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  templatesAverageScore: number;
  profilePageType: ProfilePageType;
  userDetails: UserDetails;
  accountCertifications: PublicCertification[];
}> = ({
  partnerProfile,
  services,
  categoriesForHeader,
  staticLocaleUrl,
  geo,
  language,
  languages,
  origin,
  entry,
  isMyProfile,
  enabled,
  isRenderedInModal,
  isMobileUserAgent,
  host,
  error,
  seo: { title, metaTags, links, structureData },
  userCurrency,
  currencyConversionRate,
  shouldDisplayTraceabilityData,
  taxRate,
  numberOfTemplates,
  templatesNumberOfPages,
  currentPage,
  sort,
  templatesAverageScore,
  profilePageType,
  userDetails,
  accountCertifications,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const renderProfile = useCallback(() => {
    if (error === PROFILE_NOT_FOUND) {
      return <NotFound />;
    } else if (error === PROFILE_NOT_EU_VALID) {
      return <NotValidEuEmptyState />;
    } else {
      return <Profile />;
    }
  }, [error]);

  const ProfileWithContexts = () => {
    const profile = (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        {renderProfile()}
      </WixDesignSystemProvider>
    );

    return (
      <CurrencyContextProvider
        currencyData={{
          currencyCode: userCurrency,
          conversionRate: currencyConversionRate,
        }}
        taxRate={taxRate}
      >
        {profile}
      </CurrencyContextProvider>
    );
  };

  return (
    <>
      <ProfileHeader
        title={title}
        links={links}
        metaTags={metaTags}
        structureData={structureData}
        isProd={isProd}
      />
      <BiSessionContextProvider>
        <HeaderContextProvider
          services={categoriesForHeader || services}
          host={host}
        >
          <ProfileContextProvider
            partnerProfile={partnerProfile}
            services={services}
            staticLocaleUrl={staticLocaleUrl}
            geo={geo}
            language={language}
            languages={languages}
            origin={origin}
            host={host}
            entry={entry}
            isMyProfile={isMyProfile}
            enabled={enabled}
            isRenderedInModal={isRenderedInModal}
            isMobileUserAgent={isMobileUserAgent}
            userCurrency={userCurrency}
            currencyConversionRate={currencyConversionRate}
            shouldDisplayTraceabilityData={shouldDisplayTraceabilityData}
            profilePageType={profilePageType}
            userDetails={userDetails}
            accountCertifications={accountCertifications}
          >
            <TemplatesContextProvider
              numberOfResults={numberOfTemplates}
              numberOfPages={templatesNumberOfPages}
              isMobileUserAgent={isMobileUserAgent}
              origin={origin}
              entry={entry}
              host={host}
              currentPage={currentPage}
              sort={sort}
              templatesAverageScore={templatesAverageScore ?? 0}
            >
              <ProfileWithContexts />
            </TemplatesContextProvider>
          </ProfileContextProvider>
        </HeaderContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  query,
  params,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
  params: any;
}) {
  const isProd = process.env.NODE_ENV === 'production';
  const isRenderedInModal = query?.inModal === 'true';
  const host = isProd ? `https://${req.get('host')}` : `http://localhost:3000`;

  const initialStateService = createInitialStateService(req.aspects, res);
  const servicesAPI = createServicesAPI(res.locals.appContext, req.aspects);
  const partnerTraceableTraderAPI = createPartnerTraceableTraderAPI(
    res.locals.appContext,
    req.aspects,
  );
  const templateListingAPI = createTemplateListingAPI(
    res.locals.appContext,
    req.aspects,
  );
  const userAPI = createUserAPI(res.locals.appContext, req.aspects);

  const language = getPageLanguage({ aspects: req.aspects });
  const geo = req.aspects?.get(web.Geo);
  const url = req.aspects?.get(web.Url);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];
  let shouldDisplayTraceabilityData = false;

  const currencyAPI = createCurrencyAPI(res.locals.appContext, req.aspects);

  const providersAPI = createProvidersAPI(res.locals.appContext, req.aspects);
  const languagesAPI = createLanguagesAPI(res.locals.appContext, req.aspects);
  const accountCertificationsAPI = createAccountCertificationsAPI(
    res.locals.appContext,
    req.aspects,
  );
  const initialState = await initialStateService.create();

  try {
    const { slug } = params;

    if (he.decode(slug as string) === 'wix-demo-agency') {
      const [services, languages] = await Promise.all([
        servicesAPI.getAllServicesWithHierarchy({
          withPathName: true,
          withTranslatedKey: true,
          language,
        }),
        languagesAPI.getLanguages(),
      ]);
      const partnerProfile = require('./demo-agency.json');
      const seoAPI = await createSeoAPI({
        partnerProfile,
        language,
        url,
      });
      return {
        props: {
          initialState,
          host,
          seo: {
            title: seoAPI.getPageTitle(),
            metaTags: seoAPI.getPageMetaTags(),
            links: seoAPI.getPageLinkTags(),
            structureData: JSON.parse(
              JSON.stringify(seoAPI.getProfileStructureData(partnerProfile)),
            ),
          },
          slug,
          partnerProfile,
          services,
          language,
          languages,
          numberOfTemplates: 6,
          templatesNumberOfPages: 1,
          accountCertifications: [],
        },
      };
    }

    const [
      services,
      categoriesForHeader,
      traceabilityCountries,
      languages,
      userDetails,
      loggedInAccountId,
      { userCurrency, currencyConversionRate, countryTaxRate },
    ] = await Promise.all([
      servicesAPI.getAllServicesWithHierarchy({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      servicesAPI.getCategoriesForHeader({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      partnerTraceableTraderAPI.getTraceabilityCountries(),
      languagesAPI.getLanguages(),
      userAPI.getUserDetails(),
      userAPI.getLoggedInAccountId(),
      currencyAPI.getGeoBasedTaxAndCurrencyRate({
        aspects: req.aspects,
        loggerName: 'wix-partner',
      }),
    ]);

    const isUserInTraceabilityRequiredLocale: boolean = _.includes(
      traceabilityCountries,
      alpha2CountryCode,
    );

    const partnerProfile = await providersAPI.findPartnerProfileBySlug(
      he.decode(slug as string),
      {
        isUserInTraceabilityRequiredLocale,
      },
    );

    let accountCertifications: PublicCertification[] = [];
    let haveTraceabilityViolation = false;

    if (!isPartnerProfileSlugError(partnerProfile)) {
      const traceabilityFlags = calculateTraceabilityFlags(
        isUserInTraceabilityRequiredLocale,
        partnerProfile.euValid,
      );
      shouldDisplayTraceabilityData =
        traceabilityFlags.shouldDisplayTraceabilityData;
      haveTraceabilityViolation = traceabilityFlags.haveTraceabilityViolation;
    }

    if (isPartnerProfileSlugError(partnerProfile)) {
      return {
        props: {
          initialState,
          partnerProfile: {},
          services,
          categoriesForHeader,
          error: partnerProfile?.error,
          seo: {
            title: 'Profile Not Found - Wix Marketplace',
          },
          host,
          accountCertifications: [],
        },
      };
    }

    const { entry, origin } = query;

    const isMyProfile = loggedInAccountId === partnerProfile.id;
    const seoAPI = await createSeoAPI({
      partnerProfile,
      language,
      url,
    });

    const config = res.locals.appContext.config.load<HadronConfig>(
      'experts-marketplace-nextjs-template.json',
    );
    const staticLocaleUrl = res.locals.hadron.staticUrl(
      'com.wixpress.locale-dataset-data',
      config.clientTopology.locale_dataset_data_static_url,
    );

    const [templateAggregateResult, certificationsResult] = await Promise.all([
      templateListingAPI.getPartnerTemplateAggregate({
        partnerId: partnerProfile.id!,
        page: 0,
        languages: [],
        language: 'ALL',
        features: [],
        industries: [],
        siteType: 'ALL',
        styles: [],
        price: 'ALL',
        search: '',
      }),
      partnerProfile?.id
        ? accountCertificationsAPI.getAccountCertifications(partnerProfile.id)
        : Promise.resolve([]),
    ]);

    const { templateListingCount, templatesAverageScore } =
      templateAggregateResult;
    accountCertifications = certificationsResult || [];
    const templatesNumberOfPages = templateListingCount
      ? _.ceil(templateListingCount / 6)
      : 0;
    return {
      props: {
        initialState,
        slug,
        ...(partnerProfile && {
          partnerProfile: JSON.parse(JSON.stringify(partnerProfile)),
        }),
        enabled: partnerProfile?.state?.enabled || false,
        language,
        languages,
        ...(geo && { geo }),
        services,
        categoriesForHeader,
        staticLocaleUrl,
        ...(origin && { origin }),
        ...(entry && { entry }),
        isMyProfile,
        isRenderedInModal,
        host,
        seo: {
          title: seoAPI.getPageTitle(),
          metaTags: seoAPI.getPageMetaTags(),
          links: seoAPI.getPageLinkTags(),
          structureData: JSON.parse(
            JSON.stringify(seoAPI.getProfileStructureData(partnerProfile)),
          ),
        },
        userCurrency,
        currencyConversionRate,
        shouldDisplayTraceabilityData,
        taxRate: countryTaxRate,
        numberOfTemplates: templateListingCount,
        templatesNumberOfPages,
        templatesAverageScore: templatesAverageScore ?? 0,
        currentPage: 1,
        sort: 'relevance' as SortingMethod,
        ...(userDetails && { userDetails }),
        accountCertifications,
        profilePageType: partnerProfile.profilePageType,
      },
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('wix-partner')
      .withAspects(req.aspects)
      .error(`error w/ wix-partner`, { message, stack });
    return {
      props: { initialState, message, stack, partnerProfile: {}, seo: {} },
    };
  }
}

export default WixPartner;
